import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { ChevronDown, ChevronRight, FileText } from "lucide-react";
import { paths, type CompanyDoc } from "@clevis/shared-types";
import { cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { categoryLabel, expiryState, fmtExpiry } from "./SendDocuments";
import { QuickDocUpload } from "./QuickDocUpload";

// Pick company documents to attach, grouped by folder (LiftMaster brochures,
// Insurance…), with expiry shown. Documents made out to another customer
// are left out. Used by "Send documents" and estimate / invoice emails.

export type PickedDoc = CompanyDoc & { id: string };

export function useCompanyDocs(tenantId: string, customerId: string | null, enabled = true): PickedDoc[] {
  const [docs, setDocs] = useState<PickedDoc[]>([]);
  useEffect(() => {
    if (!enabled) return;
    return onSnapshot(collection(db, paths.tenantCompanyDocs(tenantId)), (snap) =>
      setDocs(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as CompanyDoc) }))
          .filter((d) => !d.archivedAt && (!d.forCustomerId || d.forCustomerId === customerId))
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
      ),
    );
  }, [tenantId, customerId, enabled]);
  return docs;
}

export function CompanyDocPicker({
  docs,
  picked,
  onToggle,
  canManage,
  tenantId,
  customerId = null,
  onAdded,
}: {
  docs: PickedDoc[];
  picked: Set<string>;
  onToggle: (id: string) => void;
  canManage: boolean;
  /** With these, a new document can be added right here (and gets picked). */
  tenantId?: string;
  customerId?: string | null;
  onAdded?: (docId: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const canAdd = canManage && !!tenantId && !!onAdded;
  const uploader =
    adding && tenantId && onAdded ? (
      <QuickDocUpload
        tenantId={tenantId}
        customerId={customerId}
        onCancel={() => setAdding(false)}
        onSaved={(id) => {
          setAdding(false);
          onAdded(id);
        }}
      />
    ) : null;
  const folders = useMemo(() => {
    const m = new Map<string, PickedDoc[]>();
    for (const d of docs) {
      const f = d.folder?.trim() || "";
      m.set(f, [...(m.get(f) ?? []), d]);
    }
    return [...m.entries()].sort((a, b) => (a[0] === "" ? -1 : b[0] === "" ? 1 : a[0].localeCompare(b[0])));
  }, [docs]);
  const [closed, setClosed] = useState<Set<string>>(new Set());

  if (docs.length === 0) {
    return (
      <div>
        <p className="text-xs text-steel">
          No company documents yet.{" "}
          {canAdd ? (
            !adding ? (
              <button type="button" className="text-brandorange-dark underline" onClick={() => setAdding(true)}>
                Upload a W-9, certificate or brochure here
              </button>
            ) : null
          ) : (
            "An admin adds them under Company documents."
          )}
        </p>
        {uploader}
      </div>
    );
  }
  return (
    <div>
    <div className="max-h-64 overflow-y-auto rounded border border-steel/15 bg-white">
      {folders.map(([folder, list]) => {
        const isClosed = closed.has(folder);
        const chosen = list.filter((d) => picked.has(d.id)).length;
        return (
          <div key={folder || "(none)"}>
            {folders.length > 1 || folder ? (
              <button
                type="button"
                className="flex w-full items-center gap-1.5 border-b border-steel/10 bg-cream/50 px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-wide text-steel hover:text-ink"
                onClick={() =>
                  setClosed((s) => {
                    const n = new Set(s);
                    if (n.has(folder)) n.delete(folder);
                    else n.add(folder);
                    return n;
                  })
                }
              >
                {isClosed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                {folder || "No folder"} <span className="font-normal normal-case">({list.length}{chosen ? `, ${chosen} picked` : ""})</span>
              </button>
            ) : null}
            {!isClosed
              ? list.map((d) => {
                  const ex = expiryState(d.expiresAt);
                  return (
                    <label key={d.id} className="flex cursor-pointer items-center gap-2 border-b border-steel/10 px-2 py-1 text-sm last:border-0 hover:bg-cream/60">
                      <input type="checkbox" checked={picked.has(d.id)} onChange={() => onToggle(d.id)} />
                      <FileText size={13} className="shrink-0 text-steel" />
                      <span className="min-w-0 flex-1 truncate text-ink">{d.name}</span>
                      <span className={cn("shrink-0 text-xs", ex === "expired" ? "text-danger" : ex === "soon" ? "text-brandorange-dark" : "text-steel")}>
                        {d.forCustomerId ? "for this customer · " : ""}
                        {d.expiresAt ? `${ex === "expired" ? "expired" : "good through"} ${fmtExpiry(d.expiresAt)}` : categoryLabel(d.category)}
                      </span>
                    </label>
                  );
                })
              : null}
          </div>
        );
      })}
    </div>
    {canAdd && !adding ? (
      <button type="button" className="mt-1 text-xs text-brandorange-dark underline" onClick={() => setAdding(true)}>
        + Upload another document
      </button>
    ) : null}
    {uploader}
    </div>
  );
}
